import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { LoadingSpinner } from '../atoms';
import { useSelector } from 'react-redux';
import { RootState } from '@/shared/store/store';
import { shouldRedirectToOnboarding } from '@/shared/utils/onboardingValidator';
import { tieneAlgunRol } from '@/shared/utils/oficio';
import { leerSuplantacionDelToken } from '@/features/impersonation/utils/suplantacion-del-token';

export interface AuthGuardProps {
  children: React.ReactNode;
  roles?: string[];
  requireAuth?: boolean;
  redirectTo?: string;
  fallback?: React.ReactNode;
  className?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  roles = [],
  requireAuth = true,
  redirectTo = '/login',
  fallback,
  className = '',
}) => {
  const { isAuthenticated, user, loading, token } = useAuth();
  const adminState = useSelector((state: RootState) => state.admin);
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);

  // Combinar estado de autenticación normal y admin
  // Admin state takes priority — if admin is authenticated, use admin user
  const combinedLoading = loading || adminState.loading;
  const combinedIsAuthenticated = isAuthenticated || adminState.isAuthenticated;
  const combinedUser = adminState.isAuthenticated ? adminState.user : user;

  // Hook para manejar redirección de onboarding
  // NO usar el hook porque causa loops infinitos
  // La lógica de redirección está implementada más abajo en este mismo componente
  // const { } = useOnboardingRedirect();

  // Esperar a que se inicialice la autenticación
  useEffect(() => {
    if (!combinedLoading) {
      setIsInitialized(true);
    }
  }, [combinedLoading]);

  // Si está cargando o no se ha inicializado, mostrar loading
  if (combinedLoading || !isInitialized) {
    return (
      <div
        className={`flex items-center justify-center min-h-screen ${className}`}
      >
        {fallback || (
          <div className="text-center">
            <LoadingSpinner size="lg" text="Verificando autenticación..." />
          </div>
        )}
      </div>
    );
  }

  // Si no requiere autenticación, mostrar contenido
  if (!requireAuth) {
    return <>{children}</>;
  }

  // Si no está autenticado (ni normal ni admin), redirigir al login
  // EXCEPCIÓN: Permitir acceso si hay tokens de Google OAuth en la URL
  if (!combinedIsAuthenticated) {
    const hasOAuthTokens = location.search.includes('token=') &&
      location.search.includes('refreshToken=') &&
      location.search.includes('userId=');

    if (hasOAuthTokens) {
      return <>{children}</>;
    }

    // Fix #76: una ruta es admin-only cuando TODAS sus roles son admin (SUPER_ADMIN),
    // no cuando SUPER_ADMIN está incluido entre otros (ej. ERP_ROLES tiene SUPER_ADMIN
    // + AGENCY_ADMIN + ACCOUNTING_TEAM — esa NO es admin-only, es ruta mixta).
    const ADMIN_ONLY_ROLES = ['SUPER_ADMIN'];
    const isAdminRoute = roles.length > 0 && roles.every(r => ADMIN_ONLY_ROLES.includes(r));
    const adminRedirectTo = isAdminRoute ? '/admin/login' : redirectTo;
    return <Navigate to={adminRedirectTo} state={{ from: location }} replace />;
  }

  // Si el usuario necesita completar onboarding y no está en la página de onboarding
  // Usar validación inteligente que considera tanto el flag como los datos reales
  // Los super admins nunca son redirigidos al onboarding
  // EXCEPCIÓN: No redirigir si está en /email-verification (paso intermedio después del registro)
  // GH#2768 — una SUPLANTACIÓN nunca entra al onboarding de la cuenta que se está viendo: el super-admin no debe completar el alta de otro. El `user`
  // que deja `POST /admin/users/:id/impersonate` ni siquiera trae `onboardingCompleted`, y sin esto TODA suplantación caía en /onboarding. Es una
  // PISTA leída del claim del token (suplantacion-del-token.ts): ilegible o ausente = `false` = el guard de siempre.
  const esSuplantacion = leerSuplantacionDelToken(token) !== null;
  const shouldRedirect = combinedUser && 
    combinedUser.role !== 'SUPER_ADMIN' && 
    !esSuplantacion && 
    shouldRedirectToOnboarding(combinedUser) && 
    location.pathname !== '/onboarding' && 
    location.pathname !== '/email-verification';
  
  if (shouldRedirect) {
    return <Navigate to="/onboarding" replace />;
  }


  // Si requiere roles específicos
  if (roles.length > 0 && combinedUser) {
    const hasRequiredRole = tieneAlgunRol(combinedUser, roles);
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Si pasa todas las validaciones, mostrar contenido
  return <>{children}</>;
};

export default AuthGuard;
